// src/app/error.service.ts
import {
  Injectable,
  ErrorHandler,
  Injector
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import Rollbar from 'rollbar';

import { environment } from 'src/environments/environment';
import { SnackBarService } from '@services/snackbar/snack-bar-service.service';
import { DiagnosticoService } from '@services/diagnostico/diagnostico.service';

// Observabilidad (2026-07-31): GlitchTip (primario, vía @sentry/angular — Sentry.init() se
// llama en main.ts antes del bootstrap) + Rollbar (secundario, se mantiene a pedido explícito).
// Antes este handleError() no hacía nada — todo el cuerpo real estaba comentado y el paquete
// 'rollbar' ni siquiera estaba instalado. Se usa Injector en vez de inyectar los servicios
// directo en el constructor para evitar problemas de orden de inicialización tan temprano en el
// bootstrap de Angular (ErrorHandler se crea antes que casi todo lo demás).
@Injectable()
export class RollbarErrorHandlerService implements ErrorHandler {

  // Instancia única de Rollbar para todo el ciclo de vida de la app — se crea perezosamente en
  // el primer error si hay accessToken configurado, no en el constructor (mismo motivo que el
  // uso de Injector: no asumir que todo está listo tan temprano en el bootstrap).
  private rollbar: Rollbar | null = null;
  private rollbarInitIntentado = false;

  constructor(
    private injector: Injector
  ) {
  }

  handleError(err: any): void {
    const entorno = `${environment.entorno}`;

    // Resolver DiagnosticoService acá (en vez de solo confiar en que algún otro punto de la app
    // ya lo haya instanciado) garantiza que su patch sobre console.error ya esté activo antes del
    // console.error de abajo, así este mismo error también queda como breadcrumb disponible para
    // el feature "Reportar un problema".
    try {
      this.injector.get(DiagnosticoService);
    } catch {
      // no crítico
    }

    console.error('Error no controlado en el cliente:', err);

    // No mostrarle un toast al usuario por errores que ya tienen su propio manejo (ej. HttpErrorResponse,
    // que ya pasó por GlobalHttpErrorInterceptorService). Este handler es la red de contención para
    // errores de runtime "de verdad" (ej. TypeError por un null no esperado en un template o en un
    // callback de subscribe) que si no, dejaban al usuario en una pantalla trabada sin ninguna explicación.
    const esErrorHttp = !!(err?.error && (err?.status !== undefined || err?.statusText !== undefined));

    if (!esErrorHttp) {
      try {
        const snackBar = this.injector.get(SnackBarService);
        snackBar.showError(
          'Ocurrió un error inesperado en la aplicación. Si el problema persiste, usá la opción "Reportar un problema".',
          'Error'
        );
      } catch {
        // Si el snackbar no se pudo mostrar (ej. muy temprano en el bootstrap), no hay que
        // romper el manejo de errores por eso — ya quedó logueado arriba.
      }
    }

    if (entorno != "Desarrollo") {
      this.reportarATrackers(err);
    }
  }

  /**
   * GlitchTip (primario) + Rollbar (secundario). Igual que en el backend
   * (GlobalExceptionHandler.ReportToErrorTrackers), cada uno queda envuelto en su propio
   * try/catch: una falla del error tracker nunca debe tirar abajo el manejo de errores real.
   */
  private reportarATrackers(err: any): void {
    const excepcionOriginal = err?.originalError || err;

    try {
      if (environment.glitchTipDsn) {
        Sentry.captureException(excepcionOriginal);
      }
    } catch {
      // no crítico
    }

    try {
      const rollbar = this.getRollbar();
      rollbar?.error(excepcionOriginal);
    } catch {
      // no crítico — Rollbar es secundario
    }
  }

  private getRollbar(): Rollbar | null {
    if (this.rollbarInitIntentado) {
      return this.rollbar;
    }

    this.rollbarInitIntentado = true;

    if (!environment.rollbarAccessToken) {
      return null;
    }

    this.rollbar = new Rollbar({
      accessToken: environment.rollbarAccessToken,
      captureUncaught: false, // ya lo cubre este mismo ErrorHandler, evita reportar duplicado
      captureUnhandledRejections: false,
      payload: {
        environment: environment.entorno,
      },
    });

    return this.rollbar;
  }
}
